import dayjs from 'dayjs';
import React, { useMemo } from 'react';
import { Box, Button, Grid, Stack, Typography } from '@mui/material';
import { DateRange, DateRangePicker } from '@mui/x-date-pickers-pro';
import {
  IFacilitySharePayload,
  IFacilityShareTargetDraft,
  IFacilityShareWindowDraft,
} from '../../dto/FacilitySharing.dto';
import CommonObjectDropdownSelect from '../../components/MUI/Select/CommonObjectDropdownSelect';

export interface IFacilityShareStationOption {
  id: number;
  name: string;
}

const DATE_ONLY_FORMAT = 'YYYY-MM-DD';

export const toFacilityShareDateTimeLocalValue = (
  value?: dayjs.ConfigType | null,
): string => {
  if (!value) {
    return '';
  }

  return dayjs(value).format(DATE_ONLY_FORMAT);
};

const getFacilityShareRangePickerValue = (
  target: IFacilityShareWindowDraft,
): DateRange<dayjs.Dayjs | null> => {
  return [
    target.starts_at ? dayjs(target.starts_at) : null,
    target.ends_at ? dayjs(target.ends_at) : null,
  ];
};

const toFacilityShareDateValue = (nextDate: dayjs.Dayjs | null): string => {
  if (!nextDate) {
    return '';
  }

  return toFacilityShareDateTimeLocalValue(nextDate);
};

export const getDefaultFacilityShareWindowDraft = (): IFacilityShareWindowDraft => {
  const currentDate = dayjs();

  return {
    starts_at: toFacilityShareDateTimeLocalValue(currentDate),
    ends_at: toFacilityShareDateTimeLocalValue(currentDate),
  };
};

export const getDefaultFacilityShareTargetDraft = (
  targetStationId = 0,
  targetStationName?: string,
): IFacilityShareTargetDraft => {
  return {
    target_station_id: targetStationId,
    target_station_name: targetStationName,
    ...getDefaultFacilityShareWindowDraft(),
  };
};

export const isFacilityShareTargetDraftValid = (
  target: IFacilityShareTargetDraft,
): boolean => {
  if (!target.target_station_id || !target.starts_at || !target.ends_at) {
    return false;
  }

  return !dayjs(target.ends_at)
    .endOf('day')
    .isBefore(dayjs(target.starts_at).startOf('day'));
};

export const getDuplicateFacilityShareTargetStationIds = (
  targets: IFacilityShareTargetDraft[],
): number[] => {
  const counts: Record<number, number> = {};

  targets.forEach(target => {
    if (!target.target_station_id) {
      return;
    }

    counts[target.target_station_id] =
      (counts[target.target_station_id] || 0) + 1;
  });

  return Object.keys(counts)
    .map(Number)
    .filter(targetStationId => (counts[targetStationId] || 0) > 1);
};

export const buildFacilitySharePayloadFromDrafts = (
  targets: IFacilityShareTargetDraft[],
  isActive: boolean = true,
): IFacilitySharePayload => {
  return {
    is_active: isActive,
    targets: targets.filter(isFacilityShareTargetDraftValid).map(target => ({
      target_station_id: target.target_station_id,
      starts_at: dayjs(target.starts_at).startOf('day').toISOString(),
      ends_at: dayjs(target.ends_at).endOf('day').toISOString(),
    })),
  };
};

interface FacilityShareFieldsProps {
  targets: IFacilityShareTargetDraft[];
  stationOptions: IFacilityShareStationOption[];
  onChange: (targets: IFacilityShareTargetDraft[]) => void;
  disabled?: boolean;
  addButtonLabel?: string;
  emptyStateText?: string;
  stationLabel?: string;
  helperTextForTarget?: (
    target: IFacilityShareTargetDraft,
    hasDuplicateStation: boolean,
  ) => string;
}

const FacilityShareFields: React.FC<FacilityShareFieldsProps> = ({
  targets,
  stationOptions,
  onChange,
  disabled = false,
  addButtonLabel = 'Add Target',
  emptyStateText = 'Add at least one shared facility target to configure an access window.',
  stationLabel = 'Shared Facility Station',
  helperTextForTarget,
}) => {
  const mergedStationOptions = useMemo(() => {
    const optionMap: Record<number, IFacilityShareStationOption> = {};

    stationOptions.forEach(option => {
      optionMap[option.id] = option;
    });

    targets.forEach(target => {
      if (target.target_station_id && !optionMap[target.target_station_id]) {
        optionMap[target.target_station_id] = {
          id: target.target_station_id,
          name:
            target.target_station_name ||
            `Shared Facility #${target.target_station_id}`,
        };
      }
    });

    return Object.values(optionMap).sort((leftOption, rightOption) => {
      return leftOption.name.localeCompare(rightOption.name);
    });
  }, [stationOptions, targets]);

  const duplicateTargetIds = useMemo(() => {
    return getDuplicateFacilityShareTargetStationIds(targets);
  }, [targets]);

  const getNextStationOption = () => {
    const usedStationIds: Record<number, boolean> = {};

    targets.forEach(target => {
      if (target.target_station_id) {
        usedStationIds[target.target_station_id] = true;
      }
    });

    return mergedStationOptions.find(option => !usedStationIds[option.id]);
  };

  const canAddTarget = Boolean(getNextStationOption());

  const handleTargetChange = (
    index: number,
    field: keyof IFacilityShareTargetDraft,
    value: number | string,
  ) => {
    onChange(
      targets.map((target, targetIndex) => {
        if (targetIndex !== index) {
          return target;
        }

        if (field === 'target_station_id') {
          const selectedStation = mergedStationOptions.find(
            option => option.id === Number(value),
          );

          return {
            ...target,
            target_station_id: Number(value),
            target_station_name: selectedStation?.name,
          };
        }

        return {
          ...target,
          [field]: value,
        };
      }),
    );
  };

  const handleTargetDateRangeChange = (
    index: number,
    value: DateRange<dayjs.Dayjs | null>,
  ) => {
    onChange(
      targets.map((target, targetIndex) => {
        if (targetIndex !== index) {
          return target;
        }

        return {
          ...target,
          starts_at: toFacilityShareDateValue(value[0]),
          ends_at: toFacilityShareDateValue(value[1]),
        };
      }),
    );
  };

  const handleAddTarget = () => {
    const nextStation = getNextStationOption();

    if (!nextStation) {
      return;
    }

    onChange([
      ...targets,
      getDefaultFacilityShareTargetDraft(nextStation.id, nextStation.name),
    ]);
  };

  const handleRemoveTarget = (index: number) => {
    onChange(targets.filter((_, targetIndex) => targetIndex !== index));
  };

  return (
    <Stack spacing={2}>
      <Stack
        direction={{xs: 'column', md: 'row'}}
        justifyContent='space-between'
        alignItems={{xs: 'flex-start', md: 'center'}}
        spacing={1.5}>
        <Typography variant='body2' color='text.secondary'>
          Configure each shared facility station and its active window.
        </Typography>
        <Button
          variant='outlined'
          onClick={handleAddTarget}
          disabled={disabled || !canAddTarget}>
          {addButtonLabel}
        </Button>
      </Stack>

      {targets.length === 0 ? (
        <Typography variant='body2' color='text.secondary'>
          {emptyStateText}
        </Typography>
      ) : (
        targets.map((target, index) => {
          const hasDuplicateStation = Boolean(
            target.target_station_id &&
              duplicateTargetIds.includes(target.target_station_id),
          );
          const hasWindowError = !isFacilityShareTargetDraftValid(target);
          const stationHelperText = helperTextForTarget
            ? helperTextForTarget(target, hasDuplicateStation)
            : hasDuplicateStation
            ? 'Each shared facility station can only be added once.'
            : ' ';

          return (
            <Box
              key={`${target.target_station_id || 'new'}-${index}`}
              sx={{
                p: 2,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
              }}>
              <Stack spacing={2}>
                <Stack
                  direction={{xs: 'column', md: 'row'}}
                  alignItems={{xs: 'flex-start', md: 'center'}}
                  justifyContent='space-between'
                  spacing={1}>
                  <Typography variant='subtitle2'>
                    Target {index + 1}
                  </Typography>
                  <Button
                    color='error'
                    variant='text'
                    onClick={() => handleRemoveTarget(index)}
                    disabled={disabled}>
                    Remove Target
                  </Button>
                </Stack>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Stack spacing={0.75}>
                      <CommonObjectDropdownSelect
                        label={stationLabel}
                        data={mergedStationOptions}
                        selected={target.target_station_id || undefined}
                        onSelect={value =>
                          handleTargetChange(
                            index,
                            'target_station_id',
                            Number(value),
                          )
                        }
                        disabled={disabled}
                      />
                      <Typography
                        variant='caption'
                        color={
                          hasDuplicateStation ? 'error.main' : 'text.secondary'
                        }
                        sx={{minHeight: 20, px: 1.75}}>
                        {stationHelperText}
                      </Typography>
                    </Stack>
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <Stack spacing={0.75}>
                      <DateRangePicker
                        label='Active Dates'
                        value={getFacilityShareRangePickerValue(target)}
                        onChange={value =>
                          handleTargetDateRangeChange(index, value)
                        }
                        disabled={disabled}
                        localeText={{start: 'Starts At', end: 'Ends At'}}
                        sx={{width: '100%'}}
                        slotProps={{
                          textField: {
                            size: 'small',
                            error: hasWindowError,
                            sx: {width: '100%'},
                          },
                        }}
                      />
                      <Typography
                        variant='caption'
                        color={hasWindowError ? 'error.main' : 'text.secondary'}
                        sx={{minHeight: 20, px: 1.75}}>
                        {hasWindowError
                          ? 'End time must be after the start time.'
                          : ' '}
                      </Typography>
                    </Stack>
                  </Grid>
                </Grid>
              </Stack>
            </Box>
          );
        })
      )}
    </Stack>
  );
};

export default FacilityShareFields;